import { List } from "immutable";
import { PresentationRecord } from "common/records/Presentation";
import { fromJSON as dplFromJSON } from "common/records/DynamicPresentationLink";
import { SlideRecord } from "common/records/Slide";
import { DynamicPresentationLinkRecord } from "common/records/DynamicPresentationLink";
import { slide as slideSelector } from "common/selectors/slide.selectors";
import { RootState } from "common/reducers";

export const addSlides = (
  state: RootState,
  dynamicPresentation: PresentationRecord,
  presentation: PresentationRecord,
  orgPresentations: List<PresentationRecord>,
  sortOrder = "default",
): PresentationRecord => {
  presentation.slide_ids.map((slideId) => {
    dynamicPresentation = addSlide(state, dynamicPresentation, slideId, orgPresentations, sortOrder);
  });
  return dynamicPresentation;
};

export const addSlide = (
  state: RootState,
  dynamicPresentation: PresentationRecord,
  slideId: number,
  orgPresentations: List<PresentationRecord>,
  sortOrder = "default",
): PresentationRecord => {
  const presIds: List<number> = orgPresentations.map((p) => p.id);

  // Sort the slides based on presentation id
  dynamicPresentation = dynamicPresentation.update("slide_ids", (slide_ids): List<number> => {
    const updatedSlides: List<number> = slide_ids.push(slideId);

    // No need of sorting or grouping if the sort_order is free-for-all, just needs appending
    if (sortOrder === "free-for-all") {
      return updatedSlides;
    }

    const grouped = updatedSlides
      .map((slideId) => slideSelector(state, { slideId }))
      .groupBy((a) => a?.presentation_id);
    const sorted: List<List<SlideRecord>> = presIds.map((id) => {
      const list = grouped.get(id);
      if (!list) {
        return List();
      }
      return list
        .sortBy((s) => s?.sort_order)
        .toList()
        .filter((s) => s !== undefined);
    });

    return sorted
      .flatten()
      .toList()
      .map((s: any) => s.id);
  });

  // Get the inspection slide figure out where it goes.
  // Location is hardcoded to 'after' the 'first' presentation.
  const slidesById = dynamicPresentation.slide_ids;
  const dynamicPresentationSlides = dynamicPresentation.slide_ids
    .map((slideId) => slideSelector(state, { slideId }))
    .filter((s) => s !== undefined);
  const inspectionLinkIdx = dynamicPresentation.dynamic_links.findIndex((dl) => dl.kind === "inspection");
  const inspectionLink = dynamicPresentation.dynamic_links.get(inspectionLinkIdx);

  return dynamicPresentation.update("dynamic_links", (dls) => {
    // check to see if it exists as a destroyed
    const existing = dls.findIndex((dls) => dls.presentable_id === slideId && dls.presentable_type === "Slide");

    if (existing >= 0) {
      // if it's a destroyed, just undestroy it.
      dls = dls.update(existing, (d) => d?.set("_destroy", false));
    } else {
      // if it's not destroyed then add it as a new
      dls = dls.push(dplFromJSON({ presentable_id: slideId, presentable_type: "Slide" }));
    }

    // sort the dynamic links based on the slide.
    // insert the inspection where it should go
    // update sort orders
    let finalDynamicLinks = dls;
    if (sortOrder === "free-for-all") {
      // if we are in a free-for-all sorting, put the new slide at the end
      const dsSlideIdx = finalDynamicLinks.findIndex((s) => s.presentable_id === slideId);

      finalDynamicLinks = moveSlide(
        finalDynamicLinks,
        finalDynamicLinks.get(dsSlideIdx)!,
        dsSlideIdx,
        finalDynamicLinks.size,
      );
    } else {
      // otherwise do traditional sorting
      dls = dls.splice(inspectionLinkIdx, 1);
      finalDynamicLinks = dls
        .sort((a, b) => {
          if (a._destroy && b._destroy) {
            return 0;
          } else if (a._destroy && !b._destroy) {
            return 1;
          } else if (!a._destroy && b._destroy) {
            return -1;
          }

          return slidesById.indexOf(a.presentable_id!) - slidesById.indexOf(b.presentable_id!);
        })
        .insert(0, inspectionLink!)
        .map((dl, idx) => {
          return dl.set("sort_order", idx);
        });
    }
    return finalDynamicLinks;
  });
};

export const activeDynamicLinks = (presentation: PresentationRecord): List<DynamicPresentationLinkRecord> => {
  if (presentation === null || presentation === undefined) {
    return List<DynamicPresentationLinkRecord>();
  }
  return presentation.dynamic_links.filter((dl) => !dl._destroy);
};

export const slideSelected = (dynamicPresentation: PresentationRecord, slideId: number): boolean => {
  if (!dynamicPresentation) {
    return false;
  }

  return activeDynamicLinks(dynamicPresentation).some((dl) => dl.presentable_id === slideId);
};

export const getSortOrder = (
  presentation: PresentationRecord,
  orgPresentations: List<PresentationRecord>,
): "default" | "free-for-all" => {
  if (presentation === undefined || presentation === null) {
    return "default";
  }

  const newPresentationIds: number[] = [];
  const newSlideIds: number[] = [];

  // collecting ids for presentation & slides that are included in the new presentation
  presentation.slide_ids.forEach((id) => {
    newSlideIds.push(id);
    newPresentationIds.push(presentation.id);
  });

  // slides from orgPresentation that are part of new presentation being created in the default sorting order
  const defaultSortedSlides = orgPresentations
    .filter(({ id }) => newPresentationIds.includes(id))
    .flatMap(({ slide_ids }) => slide_ids)
    .filter((id) => newSlideIds.includes(id));

  // if the new presentation slides are in the same order as of the org presentation slides listing then it's the default order
  if (defaultSortedSlides.every((id, index) => newSlideIds[index] === id)) {
    return "default";
  } else {
    return "free-for-all";
  }
};

export const removeSlides = (
  dynamicPresentation: PresentationRecord,
  presentation: PresentationRecord,
): PresentationRecord => {
  presentation.slide_ids.map((slideId) => {
    if (slideSelected(dynamicPresentation, slideId)) {
      dynamicPresentation = removeSlide(dynamicPresentation, slideId);
    }
  });
  return dynamicPresentation;
};

export const removeSlide = (dynamicPresentation: PresentationRecord, slideId: number): PresentationRecord => {
  return dynamicPresentation
    .update("dynamic_links", (dls) => {
      const index = dls.findIndex((dl) => dl.presentable_id === slideId);
      const dl = dls.get(index);
      if (dl && dl.id <= 0) {
        return dls.filter((dl) => dl.presentable_id !== slideId).map((dl, idx) => dl.set("sort_order", idx));
      } else {
        return dls.update(index, (dl) => dl?.set("_destroy", true));
      }
    })
    .update("slide_ids", (slide_ids) => {
      return slide_ids.filter((slide_id) => slide_id !== slideId);
    });
};

export const moveSlide = (
  allSlides: List<DynamicPresentationLinkRecord>,
  slide: DynamicPresentationLinkRecord,
  fromIndex: number,
  toIndex: number,
): List<DynamicPresentationLinkRecord> => {
  allSlides = allSlides
    .delete(fromIndex)
    .insert(toIndex, slide)
    .map((a, idx) => {
      return a.set("sort_order", idx);
    });

  return allSlides;
};
